import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';
import styled, { useTheme } from 'styled-components';
import { Button } from 'components/Button';
import { DateCard } from 'components/DateCard';
import Icon from 'components/Icon';
import { Modal } from 'components/Modal';
import Spacer from 'components/Spacer';
import { useDebounceCallback } from 'hooks/useDebounceCallback';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { useHoldBestAvailable } from './gql/mutation/useHoldBestAvailable';
import { useReleaseBestAvailable } from './gql/mutation/useReleaseBestAvailable';
import { SeatSelectionModal } from './SeatSelectionModal';
import { TimerCountdown } from './TimerCountdown';

const HeaderContainer = styled.div`
  background-color: rgb(0, 0, 31);
  color: white;
`;

const Header = styled.div`
  background: #1a1a47;
  padding: 10px;
  justify-content: start;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 60px;
`;

const EventName = styled.span`
  //styleName: PP - H3;
  font-family: Barlow Condensed;
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  text-align: left;
  color: #ffffff;
`;

const FooterContainer = styled.div`
  height: 140px;
  background-color: #fa4616;
  display: flex;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  color: white;
  flex: 1;

  @media (max-width: 536px) {
    padding-right: 5px;
    padding-inline: 10px;
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 10px;

  /* Align vertically on mobile */
  @media (max-width: 536px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
`;

const ItemPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  align-items: center;
  margin-right: 6px;
`;

const Count = styled.div`
  background-color: white;
  border-radius: 50%;
  color: black;
  padding: 5px;
  width: 15px;
  height: 15px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CounterContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin: 10px 0 5px 0;
`;

const CountTextContainer = styled.div`
  width: 48px;
  height: 40px;
  padding: 0px, 18px, 0px, 18px;
  border-radius: 8px;
  gap: 10px;
  display: flex;
  justify-content: center;
`;

const CountText = styled.span`
  font-family: Barlow Condensed;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: 0px;
  text-align: center;
  align-items: center;
  display: flex;
`;

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const CART_TIMEOUT = 600000;

// Get the total price of the selected tickets which is in the format of $XX.XX
// and return in the format of XX.XX
const getTotalPrice = (selectedTicket) =>
  selectedTicket?.seats.reduce((acc, seat) => {
    const rawPrice = seat?.price ?? selectedTicket?.price;
    const numeric = parseFloat(String(rawPrice).replace('$', ''));
    return acc + (isNaN(numeric) ? 0 : numeric);
  }, 0) ?? 0;

export default function SeatsManagerModal({ isVisible, setIsVisible }) {
  const {
    seatId,
    addTicketSeat,
    removeTicketSeat,
    chartRef,
    clearTicket,
    tickets,
    selected,
    event,
    bestTickets,
    setBestTickets,
    bestSeats,
    setBestSeats,
    clearBestTickets,
    selectedSeats,
    setSelectedSeats,
  } = useCart();

  const {
    state: { navIsExpanded },
  } = useContext(AppContext);

  const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
  const holdToken = seatsio?.holdToken;

  const selectedSeatId = localStorage.getItem('selectedTicket');
  const selectedTicket = tickets[selected]?.tickets?.find(
    (ticket) => ticket.id === +selectedSeatId
  );

  const seat_ids = selectedTicket?.seat_ids ?? [seatId];
  const seatIdKey = seat_ids.join('-');

  const holdBestAvailable = useHoldBestAvailable();
  const releaseBestAvailable = useReleaseBestAvailable();

  const [seatSelectionIsVisible, setSeatSelectionIsVisible] = useState(false);
  const [seatSelectionMode, setSeatSelectionMode] = useState('fromMap');

  const [isFetching, setIsFetching] = useState(false);
  const [isRendering, setIsRendering] = useState(true);

  const [selectedTicketCount, setSelectedTicketCount] = useState(
    selectedTicket?.seats ? selectedTicket.seats.length : 0
  );
  const [selectedTicketTotal, setSelectedTicketTotal] = useState(
    selectedTicket?.seats ? getTotalPrice(selectedTicket) : 0
  );

  // State to hold the seat object for the selected seat before rendering for the seat map
  const [seatObject, setSeatObject] = useState([]);

  const [pendingUpdates, setPendingUpdates] = useState({});

  const onAddTicket = () => {
    setBestTickets((prev) => {
      const prevCount = prev[seatIdKey] || 0;
      const newBestTickets = {
        ...prev,
        [seatIdKey]: prevCount + 1,
      };

      setPendingUpdates(newBestTickets);

      onHoldBestAvailable(prevCount + 1);
      return newBestTickets;
    });
  };

  const onRemoveTicket = () => {
    setBestTickets((prev) => {
      const prevCount = prev[seatIdKey] || 0;

      if (prevCount === 0) return prev;

      const newBestTickets = { ...prev, [seatIdKey]: prevCount - 1 };

      if (prev[seatIdKey] === 1) {
        onReleaseBestAvailable();

        setBestSeats((prev) => {
          const newBestSeats = new Set([...prev]);
          newBestSeats.delete(seatIdKey);
          return newBestSeats;
        });

        setSelectedSeats((prev) => {
          const newSelectedSeats = { ...prev };

          newSelectedSeats[seatIdKey] = [];
          return newSelectedSeats;
        });

        // Clear the selected seats
        clearBestTickets(seat_ids);

        return { ...prev, [seatIdKey]: 0 };
      } else {
        onHoldBestAvailable(prev[seatIdKey] - 1);

        return newBestTickets;
      }
    });
  };

  const onFromMap = () => {
    setSeatSelectionMode('fromMap');
  };

  const onBestAvailable = () => {
    setSeatSelectionMode('bestAvailable');
  };

  const [timer, setTimer] = useState(CART_TIMEOUT);

  const seatCategory = useMemo(() => {
    return tickets[selected]?.tickets
      ?.filter(
        (ticket) => ticket.reserved_seat && ticket.ticket_tiers?.length > 0
      )
      ?.map((ticket) => {
        const ticketTypes = ticket.ticket_tiers.map((tier) => ({
          ticketType: tier.ticketType,
          label: tier.label,
          price: currency.format(
            tier.track_price ? tier.track_price : tier.price
          ),
          id: tier.id,
        }));
        return {
          category: ticket.reserved_seat_category,
          ticketTypes,
        };
      });
  }, [tickets, selected]);

  const theme = useTheme();

  const isMobile = useMediaQuery({ query: '(max-width: 860px)' });

  const onClose = () => {
    if (chartRef.current?.chart) chartRef.current.chart.resetView();

    // Add a delay of 1 second before closing the modal
    setTimeout(() => {
      setIsVisible(false);
    }, 1000);
  };

  const onObjectSelected = (seat) => {
    // Get the selected id from seatCategory based on the selected ticket type
    const selectedTicketType = seatCategory
      ?.find((category) => category.category === seat.category.label)
      ?.ticketTypes.find((type) => type.ticketType === seat.selectedTicketType);

    addTicketSeat(
      {
        id: seat.id,
        labels: seat.labels,
        category: seat.category,
        type: selectedTicketType?.id,
        price: selectedTicketType?.price,
      },
      seat_ids
    );
  };

  const onReleaseHoldSucceeded = (seats) => {
    if (!seats || seats.length === 0) return;

    const seat = seats.length ? seats[seats.length - 1] : seats;
    removeTicketSeat(
      {
        id: seat.id,
        labels: seat.labels,
        category: seat.category,
      },
      seat_ids
    );

    // Check if the seat is in the best available seats
    // If it is, release the best available seat

    if (bestSeats.has(seat.id)) {
      setSelectedSeats((prev) => {
        const updatedSeats = prev[seatIdKey]?.filter(
          (selectedSeat) => selectedSeat.label !== seat.id
        );

        return {
          ...prev,
          [seatIdKey]: updatedSeats,
        };
      });
    }
  };

  const onHoldTokenExpired = () => {
    clearTicket();
  };

  let item = null;
  if (event?.start_date) {
    item = {
      date: moment(event?.start_date, 'MM-DD-YYYY').format('MMM D'),
      day: moment(event?.start_date, 'MM-DD-YYYY').format('ddd'),
      month: moment(event?.start_date, 'MM-DD-YYYY').format('MMM'),
      end_date: moment(event?.end_date, 'MM-DD-YYYY').format('MM-DD-YYYY'),
      isMultiDay: event?.isMultiDay ?? true,
      listDates:
        moment(event?.start_date, 'MM-DD-YYYY').format('DD') +
        ' - ' +
        (event?.end_date
          ? moment(event?.end_date, 'MM-DD-YYYY').format('DD')
          : ''),
    };
  }

  useEffect(() => {
    if (isVisible) {
      setIsRendering(true);
    }
  }, [isVisible]);

  // Set the seat object only once when the modal is opened
  useEffect(() => {
    if (isVisible && selectedSeats[seatIdKey]?.length > 0) {
      setSeatObject(selectedSeats[seatIdKey]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const intervalRef = useRef();

  useEffect(() => {
    let interval;
    const timer = () => {
      if (!holdToken) return;

      const storedTimer = localStorage.getItem(`timer - ${holdToken}`);
      const currentTime = new Date().getTime();
      let expiryTime;

      if (storedTimer) {
        expiryTime = parseInt(storedTimer, 10);
      } else {
        expiryTime = currentTime + CART_TIMEOUT; // 10 minutes from now
        // Remove any old timer keys
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('timer')) localStorage.removeItem(key);
        });
        localStorage.setItem(`timer - ${holdToken}`, expiryTime);
      }

      intervalRef.current = setInterval(() => {
        const timeLeft = expiryTime - new Date().getTime();
        if (timeLeft <= 0) {
          clearInterval(interval);
          setIsVisible(false);
          setTimer(CART_TIMEOUT);
          clearTicket();
        } else {
          setTimer(timeLeft);
        }
      }, 1000);
    };

    if (isVisible) {
      timer();
    }

    return () => {
      clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, setIsVisible, setTimer, clearTicket]);

  // Update selected seats count and total when the selected seats change
  useEffect(() => {
    if (selectedTicket?.seats) {
      setSelectedTicketCount(selectedTicket.seats.length);
      setSelectedTicketTotal(getTotalPrice(selectedTicket));
    } else {
      setSelectedTicketCount(0);
      setSelectedTicketTotal(0);
    }
  }, [selectedTicket]);

  const fetchBestAvailableSeats = async (bestTickets) => {
    setIsFetching(true);

    // Clear the selected seats before fetching best available
    clearBestTickets(seat_ids);

    if (holdToken) {
      const response = await holdBestAvailable({
        hold_token: holdToken,
        seats_event_id: seat_ids[0],
        quantity: bestTickets,
        selected_seats: selectedTicket?.seats
          ? selectedTicket?.seats?.map((seat) => seat.id)
          : null,
      });

      const seats = response?.data?.holdBestAvailable;

      setBestTickets((prev) => {
        const current = prev[seatIdKey] ?? null;
        if (current === seats?.length) return prev;

        return {
          ...prev,
          [seatIdKey]: seats?.length,
        };
      });

      if (seats && seats.length > 0 && seats[0]?.ticketType) {
        // Add the best available seats to the selected seats
        setBestSeats(
          new Set([...bestSeats, ...seats.map((seat) => seat.label)])
        );
      }

      // Check if the response seat has ticketType
      const hasTicketType = !!seats[0]?.ticketType;

      if (seats && hasTicketType) {
        setIsRendering(true);
        setSelectedSeats((selectedSeats) => {
          const updatedSeats = { ...selectedSeats };

          // Add the new seats to the selected seats
          updatedSeats[seatIdKey] = [
            ...seats.map((seat) => ({
              label: seat.label,
              ticketType: seat.ticketType,
            })),
          ];

          selectSeatsOnChart(updatedSeats[seatIdKey]);

          return updatedSeats;
        });
      }
    }

    setPendingUpdates({});
    setIsFetching(false);
  };

  const selectSeatsOnChart = async (seatIds) => {
    if (!chartRef.current?.chart || !seatIds?.length) return;

    const chart = chartRef.current.chart;

    // Wait a bit more for chart to be fully ready
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Try selecting multiple seats at once
      if (typeof chart.selectObjects === 'function') {
        // Try bulk selection
        await chart.selectObjects(seatIds);
      } else {
        // Individual selection
        for (const seatId of seatIds) {
          await chart.selectObject(seatId);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }
    } catch (err) {
      console.error('❌ Selection failed:', err);
      toast.error('Error selecting seats. Please try again.');
    } finally {
      setIsRendering(false);
    }
  };

  const onReleaseBestAvailable = async () => {
    if (holdToken) {
      const response = await releaseBestAvailable({
        seats_event_id: seat_ids[0],
        selected_seats: selectedTicket?.seats
          ? selectedTicket?.seats?.map((seat) => seat.id)
          : null,
      });

      if (!response || response?.data?.releaseBestAvailable === null) {
        toast.error('Error releasing seats');
      }
    }
  };

  const onHoldBestAvailable = useDebounceCallback((bestTickets) => {
    fetchBestAvailableSeats(bestTickets);
  }, 1000);

  const CounterButtons = useMemo(
    () => (
      <CounterContainer>
        <Button
          buttonStyle={{ width: 46, height: 40, fontSize: 32 }}
          onClick={onRemoveTicket}
          disabled={bestTickets[seatIdKey] === 0 || isRendering || isFetching}
        >
          <span style={{ fontSize: 32 }}>-</span>
        </Button>
        <CountTextContainer>
          <CountText>
            {pendingUpdates[seatIdKey] !== undefined
              ? pendingUpdates[seatIdKey]
              : bestTickets[seatIdKey] ?? 0}
          </CountText>
        </CountTextContainer>
        <Button
          buttonStyle={{ width: 46, height: 40 }}
          onClick={onAddTicket}
          disabled={isRendering || isFetching}
        >
          {' '}
          <Icon icon="plus" size={20} color={'#fff'} />
        </Button>
      </CounterContainer>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bestTickets, seatIdKey, isRendering, isFetching, pendingUpdates]
  );

  const OptionButtons = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 15,
          marginTop: isMobile ? 35 : 10,
          flex: 1,
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            border:
              seatSelectionMode === 'fromMap'
                ? '1px solid white'
                : '1px solid #000033',
            borderRadius: 8,
          }}
        >
          <Button
            buttonStyle={{
              backgroundColor:
                seatSelectionMode === 'fromMap' ? '#000033' : 'white',
              borderRadius: 8,
              height: 50,
              padding: isMobile ? '0 3px' : '0 16px',
            }}
            textColor={seatSelectionMode === 'fromMap' ? 'white' : '#000033'}
            onClick={onFromMap}
          >
            {' '}
            From map
          </Button>
        </div>

        <div
          style={{
            border:
              seatSelectionMode === 'fromMap'
                ? '1px solid #fa4616'
                : '1px solid white',
            borderRadius: 8,
          }}
        >
          <Button
            buttonStyle={{
              backgroundColor:
                seatSelectionMode === 'fromMap' ? 'white' : '#fa4616',
              borderRadius: 8,
              height: 50,
              padding: isMobile ? '0 3px' : '0 16px',
            }}
            textColor={seatSelectionMode === 'fromMap' ? '#fa4616' : 'white'}
            onClick={onBestAvailable}
          >
            {' '}
            Best Available
          </Button>
        </div>
      </div>
    ),
    [seatSelectionMode, isMobile]
  );

  return (
    <Modal
      maxWidth={600}
      overlayStyle={{
        marginLeft: isMobile ? 0 : !navIsExpanded ? 50 : 220,
        zIndex: 10,
      }}
      modalStyle={{
        height: '100dvh',
        maxHeight: '100dvh',
        padding: 0,
        right: 0,
        top: 0,
        maxWidth: isMobile
          ? '100vw'
          : !navIsExpanded
          ? 'calc(100vw - 50px)'
          : 'calc(100vw - 220px)',
        margin: '0',
        marginBottom: 10,
        borderRadius: 0,
        width: isMobile ? '100vw' : '100%',
        overflowY: 'auto', // Allow vertical scrolling if content overflows
      }}
      isVisible={isVisible}
      hideModal={() => setIsVisible(false)}
      noHeader={true}
    >
      <HeaderContainer>
        <Header>
          <div style={{ minWidth: 100 }}>
            {item !== null && (
              <DateCard
                item={item}
                margin={'margin: 0 10px 0 0'}
                horizontal={!isMobile ? true : false}
                isCartList={!isMobile ? true : false}
              />
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'start',
            }}
          >
            <EventName>{event?.name}</EventName>
          </div>
        </Header>
      </HeaderContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          marginBottom: -35,
          marginTop: 10,
        }}
      >
        <div
          style={{
            transform: 'rotate(45deg)',
            display: 'flex',
            justifyContent: 'end',
            marginRight: 20,
            zIndex: 1000,
          }}
        >
          <Icon
            icon="plus"
            color={theme.colors.primary}
            size={24}
            onClick={onClose}
            style={{
              transform: 'rotate(45deg)',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          />
        </div>
      </div>
      <div
        style={{
          height: `${window.innerHeight - 77}px`, // Adjusted height for better fit
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto', // Ensures scrolling
          minHeight: 400,
        }}
      >
        {event?.best_available && seat_ids.length === 1 && (
          <>
            {OptionButtons}

            {seatSelectionMode === 'bestAvailable' ? (
              CounterButtons
            ) : (
              <Spacer size={56} />
            )}
          </>
        )}

        {seat_ids && seat_ids.length > 0 && isVisible && (
          <SeatsioSeatingChart
            ref={chartRef}
            workspaceKey={process.env.REACT_APP_SEAT_WORKSPACE_KEY}
            events={seat_ids ?? ''}
            region="na"
            session={holdToken ? 'manual' : 'start'}
            onReleaseHoldSucceeded={onReleaseHoldSucceeded}
            onHoldFailed={(seats) => {
              console.error('Error holding seats', seats);
              // Release the best available seats
              seats.forEach((seat) => {
                onReleaseHoldSucceeded(seat);
              });

              toast.error('Error holding seats');
              setIsFetching(false);
            }}
            onHoldTokenExpired={onHoldTokenExpired}
            holdToken={holdToken ? holdToken : ''}
            pricing={seatCategory}
            showFullScreenButton={false}
            onObjectSelected={onObjectSelected}
            selectedObjects={seatObject ?? []}
            mode={seatSelectionMode === 'bestAvailable' ? 'static' : 'normal'}
            onChartRendered={() => {
              // Delay for a second to allow the chart to render
              setTimeout(() => {
                setIsRendering(false);
              }, 1000);
            }}
            onChartRerenderingStarted={() => {
              setIsRendering(true);
            }}
          />
        )}
        <FooterContainer>
          <Footer>
            <LeftWrapper>
              <ItemPrice>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 5,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Count>{selectedTicketCount ?? 0}</Count>
                  Selected
                </div>
                <div style={{ fontSize: 32 }}>{`${
                  selectedTicketCount
                    ? '$' + selectedTicketTotal.toFixed(2)
                    : '$0.00'
                }`}</div>
              </ItemPrice>
            </LeftWrapper>

            <div
              style={{
                maxHeight: 80,
                display: 'flex',
                flexDirection: 'row',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <TimerCountdown timer={timer} />
              <Button
                buttonStyle={{
                  backgroundColor: 'white',
                  borderRadius: 8,
                  flex: 1,
                  height: 80,
                  padding: isMobile ? '0 3px' : '0 16px',
                }}
                textColor={'#fa4616'}
                onClick={() => setSeatSelectionIsVisible(true)}
              >
                <div
                  style={{
                    fontSize: isMobile ? '20px' : '24px',
                    fontWeight: 700,
                    lineHeight: isMobile ? '24px' : '34px',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    alignItems: 'center',
                  }}
                >
                  Add to Cart
                  <div style={{ transform: 'rotate(-90deg)', width: 22 }}>
                    <Icon size={22} icon="chevron" color="#fa4616" />
                  </div>
                </div>
              </Button>
            </div>
          </Footer>
        </FooterContainer>

        <SeatSelectionModal
          isVisible={seatSelectionIsVisible}
          setIsVisible={setSeatSelectionIsVisible}
          onClose={onClose}
          seatIdKey={seatIdKey}
          seatCategory={seatCategory}
        />
      </div>
    </Modal>
  );
}
